import {
  GreenscreensTooltipConstructor,
  LaneHistoryTooltipConstructor,
  PriceRangeType,
  QuoteCardType,
} from 'components/QuoteCard';
import AljexLogo from 'icons/Aljex';
import DATLogo from 'icons/DAT';
import GlobalTranzLogo from 'icons/GlobalTranz';
import KencoFlashLogo from 'icons/KencoFlashLogo';
import McleodLogo from 'icons/McleodEnterprise';
import MercuryGateLogo from 'icons/MercuryGateLogo';
import PLSLogo from 'icons/PLSLogo';
import RevenovaLogo from 'icons/RevenovaLogo';
import SONARLogo from 'icons/SONARLogo';
import TransfixLogo from 'icons/TransfixLogo';
import TrifectaLogo from 'icons/TrifectaLogo';
import TriumphGSLogo from 'icons/TriumphGSLogo';
import TruckstopLogo from 'icons/TruckstopLogo';
import TurvoLogo from 'icons/Turvo';
import {
  type CalculatedQuote,
  LaneHistoryResponse,
  type SourceHistory,
} from 'lib/api/getLaneHistory';
import {
  QuickQuoteResponseQuote,
  SuperDispatchInternalLaneContextStatus,
} from 'lib/api/getQuickQuote';
import { Maybe, Undef } from 'types/UtilityTypes';
import { Quoting, TMS, integrationNameMap } from 'types/enums/Integrations';

import {
  LaneTier,
  ParseQuoteCardsFromResponseProps,
  QuoteFormStop,
  QuoteTypeInSource,
  SelectedQuoteType,
} from '../types';
import { buildDATTooltipFields } from './buildDATTooltipFields';
import {
  buildGreenscreensPriceRanges,
  resolveGreenscreensTripCost,
} from './buildGreenscreensPriceRanges';
import { convertFormStopsToResponseStops, toTitleCase } from './formatting';
import { parseTruPayQuoteCardFromResponse } from './trupayQuoteCard';

type LaneHistoryCard = {
  cardType: SelectedQuoteType;
  costPerMile: Undef<number>;
  priceRangePerMile: Maybe<PriceRangeType>;
};

function selectedQuoteTypeForDAT(
  quoteType: Undef<QuoteTypeInSource>
): SelectedQuoteType {
  if (quoteType === QuoteTypeInSource.DAT_LONGEST_LEG) {
    return SelectedQuoteType.DAT_LONGEST_LEG;
  }
  if (quoteType === QuoteTypeInSource.DAT_LEG_TO_LEG) {
    return SelectedQuoteType.DAT_LEG_TO_LEG;
  }
  return SelectedQuoteType.DAT_RATEVIEW;
}

function subtitleForDAT(quoteType: Undef<QuoteTypeInSource>): string {
  if (quoteType === QuoteTypeInSource.DAT_LONGEST_LEG) {
    return 'Longest Leg';
  }
  if (quoteType === QuoteTypeInSource.DAT_LEG_TO_LEG) {
    return 'Leg-to-Leg';
  }
  return '';
}

/**
 * Sort quote cards: TruPay first, then Kenco 50/50, DAT-style, then the rest.
 * The backend only returns TruPay for services with a TruPay integration.
 */
export function sortQuoteCardsForDisplay(
  cards: QuoteCardType[]
): QuoteCardType[] {
  const trupayCards = cards.filter((c) => c.type === SelectedQuoteType.TRUPAY);
  const withoutTrupay = cards.filter(
    (c) => c.type !== SelectedQuoteType.TRUPAY
  );
  return [...trupayCards, ...sortDATCardsFirst(withoutTrupay)];
}

/**
 * Sort quote cards: Kenco 50/50 average first, then DAT-style cards, then the rest.
 */
export function sortDATCardsFirst(cards: QuoteCardType[]): QuoteCardType[] {
  const isDATType = (card: QuoteCardType) =>
    card.type === SelectedQuoteType.DAT_RATEVIEW ||
    card.type === SelectedQuoteType.DAT_LONGEST_LEG ||
    card.type === SelectedQuoteType.DAT_LEG_TO_LEG;

  const kencoAverageCards = cards.filter(
    (c) => c.type === SelectedQuoteType.KENCO_FLASH_AVERAGE
  );
  const datCards = cards.filter(isDATType);
  const otherCards = cards.filter(
    (c) => !isDATType(c) && c.type !== SelectedQuoteType.KENCO_FLASH_AVERAGE
  );
  return [...kencoAverageCards, ...datCards, ...otherCards];
}

/**
 * Creates quote cards from DAT quotes
 */
export function getQuoteCards({
  quotes,
  updatedFormValues,
  isMultiStop,
}: {
  quotes: QuickQuoteResponseQuote[];
  updatedFormValues: any;
  isMultiStop: boolean;
}): QuoteCardType[] {
  const mapSelectedType = (qType: QuoteTypeInSource): SelectedQuoteType => {
    switch (qType) {
      case QuoteTypeInSource.DAT_LONGEST_LEG:
        return SelectedQuoteType.DAT_LONGEST_LEG;
      case QuoteTypeInSource.DAT_LEG_TO_LEG:
        return SelectedQuoteType.DAT_LEG_TO_LEG;
      default:
        return SelectedQuoteType.DAT_RATEVIEW;
    }
  };

  const mapSubtitle = (qType: QuoteTypeInSource): string => {
    switch (qType) {
      case QuoteTypeInSource.DAT_LONGEST_LEG:
        return 'Longest Leg';
      case QuoteTypeInSource.DAT_LEG_TO_LEG:
        return 'Leg-to-Leg';
      default:
        return '';
    }
  };

  return quotes
    .filter((q) => q.source === Quoting.DAT)
    .map((quote) => {
      const qType = quote.type as QuoteTypeInSource;
      const stopFeeUSDMedium =
        quote.metadata?.stopFeeUSD ?? quote.metadata?.stopFeeUSDMedium;
      return {
        type: mapSelectedType(qType),
        title: 'RateView',
        subtitle: mapSubtitle(qType),
        icon: <DATLogo width={64} className='inline-block' />,
        cost: quote.rates.target,
        costPerMile: quote.rates.targetPerMile,
        priceRange: {
          lowEstimate: quote.rates.low,
          highEstimate: quote.rates.high,
        } as PriceRangeType,
        confidence: quote.metadata?.confidenceLevel || null,
        priceRangePerMile: {
          lowEstimate: quote.rates.lowPerMile,
          highEstimate: quote.rates.highPerMile,
        } as PriceRangeType,
        ...buildDATTooltipFields({
          quote,
          qType,
          isMultiStop,
          stops: updatedFormValues.stops,
          stopFeeUSDMedium: stopFeeUSDMedium ?? undefined,
        }),
        inputtedTransportType: updatedFormValues.transportType,
        actualTransportType: updatedFormValues.transportType,
        subTransportType: updatedFormValues.subTransportType,
        quotingIntegrationName: SelectedQuoteType.DAT_RATEVIEW,
        quoteId: quote.id,
        isMultiStop,
        legs: quote.metadata?.legs || undefined,
      } as QuoteCardType;
    });
}

/**
 * Parses quote cards from the quick quote response
 */
export function parseQuoteCardsFromResponse({
  newQuote,
  formValues,
  setDATFuelSurcharge,
}: ParseQuoteCardsFromResponseProps): QuoteCardType[] {
  const newQuoteCards: QuoteCardType[] = [];
  if (!newQuote?.quotes?.length) {
    return newQuoteCards;
  }
  const isMultiStop = newQuote.stops.length > 2;
  // Prefer the user's granular equipment selection from form state; fall back to the
  // response echo. Drives the "Equipment" label on the quote cards when set.
  const subTransportType =
    formValues.subTransportType ?? newQuote.inputtedSubTransportType;

  newQuote.quotes.forEach((quote) => {
    if (quote.source === Quoting.KitCoach) {
      return;
    }
    switch (quote.source) {
      case Quoting.Greenscreens: {
        const isBuyPower = quote.type === QuoteTypeInSource.GS_BuyPower;
        const isZipCodeLookup = newQuote.isZipCodeLookup;
        // Greenscreens supports HOTSHOT natively, so if input was HOTSHOT, actual is HOTSHOT
        // Otherwise, use submittedTransportType (which handles Box Truck -> Van proxying)
        const greenscreensActualTransportType =
          formValues.transportType === 'HOTSHOT'
            ? formValues.transportType
            : newQuote.submittedTransportType;
        const { priceRange, priceRangePerMile } = buildGreenscreensPriceRanges(
          quote.rates,
          quote.distance
        );
        newQuoteCards.push({
          type: isBuyPower
            ? SelectedQuoteType.GS_BUYPOWER
            : SelectedQuoteType.GS_NETWORK,
          title: isBuyPower ? 'Verified Buy Rate' : 'Market Rate',
          tooltipContent: {
            isMultiStop: formValues.stops.length > 2,
            stops: formValues.stops,
            typeInSource: quote.type,
            isZipCodeLookup,
          },
          tooltipConstructor: () =>
            GreenscreensTooltipConstructor(isZipCodeLookup),
          icon: (
            <TriumphGSLogo height={16} width={75} className='inline-block' />
          ),
          cost: resolveGreenscreensTripCost(quote.rates, quote.distance),
          costPerMile: quote.rates.targetPerMile,
          confidence: quote.metadata?.confidenceLevel || null,
          priceRange,
          priceRangePerMile,
          inputtedTransportType: formValues.transportType,
          subTransportType,
          actualTransportType: greenscreensActualTransportType,
          quotingIntegrationName: toTitleCase(Quoting.Greenscreens),
          quoteId: quote.id,
          stops: convertFormStopsToResponseStops(formValues.stops),
        });
        break;
      }
      case Quoting.DAT: {
        const stopFeeUSDMedium =
          quote.metadata?.stopFeeUSD ?? quote.metadata?.stopFeeUSDMedium;
        if (quote.metadata?.fuelSurchargePerMile) {
          setDATFuelSurcharge(quote.metadata?.fuelSurchargePerMile);
        }

        newQuoteCards.push({
          type: selectedQuoteTypeForDAT(quote.type),
          title: 'RateView',
          subtitle: subtitleForDAT(quote.type),
          icon: <DATLogo width={64} className='inline-block' />,
          cost: quote.rates.target,
          costPerMile: quote.rates.targetPerMile,
          priceRange: {
            lowEstimate: quote.rates.low,
            highEstimate: quote.rates.high,
          } as PriceRangeType,
          confidence: quote.metadata?.confidenceLevel || null,
          priceRangePerMile: {
            lowEstimate: quote.rates.lowPerMile,
            highEstimate: quote.rates.highPerMile,
          } as PriceRangeType,
          ...buildDATTooltipFields({
            quote,
            qType: quote.type as QuoteTypeInSource,
            isMultiStop,
            stops: formValues.stops,
            stopFeeUSDMedium: stopFeeUSDMedium ?? undefined,
          }),
          inputtedTransportType: formValues.transportType,
          subTransportType,
          actualTransportType: newQuote.submittedTransportType,
          quotingIntegrationName: SelectedQuoteType.DAT_RATEVIEW,
          quoteId: quote.id,
          isMultiStop,
          stops: convertFormStopsToResponseStops(formValues.stops),
          legs: quote.metadata?.legs || undefined,
        });
        break;
      }
      case Quoting.TruckStop: {
        const isTruckstopBooked =
          quote.type === QuoteTypeInSource.TruckStop_Booked;
        newQuoteCards.push({
          type: isTruckstopBooked
            ? SelectedQuoteType.TRUCKSTOP_BOOKED
            : SelectedQuoteType.TRUCKSTOP_POSTED,
          title: isTruckstopBooked ? 'Booked Quote' : 'Posted Quote',
          icon: (
            <TruckstopLogo width={30} className='inline-block' fill='#D90119' />
          ),
          cost: quote.rates.target,
          costPerMile: quote.rates.targetPerMile,
          confidence: quote.metadata?.confidenceLevel || null,
          priceRange: {
            lowEstimate: quote.rates.low,
            highEstimate: quote.rates.high,
          } as PriceRangeType,
          inputtedTransportType: formValues.transportType,
          subTransportType,
          actualTransportType: newQuote.submittedTransportType,
          quotingIntegrationName: toTitleCase(Quoting.TruckStop),
          quoteId: quote.id,
          isMultiStop,
          stops: convertFormStopsToResponseStops(formValues.stops),
          legs: quote.metadata?.legs || undefined,
        });
        break;
      }
      case Quoting.KencoFlash: {
        if (quote.type !== QuoteTypeInSource.KencoAverage) {
          break;
        }

        newQuoteCards.push({
          type: SelectedQuoteType.KENCO_FLASH_AVERAGE,
          title: 'Flash Avg.',
          icon: <KencoFlashLogo />,
          cost: quote.rates.target,
          costPerMile: quote.rates.targetPerMile,
          confidence: null,
          priceRange: {
            lowEstimate: quote.rates.low,
            highEstimate: quote.rates.high,
          } as PriceRangeType,
          priceRangePerMile: {
            lowEstimate: quote.rates.lowPerMile,
            highEstimate: quote.rates.highPerMile,
          } as PriceRangeType,
          inputtedTransportType: formValues.transportType,
          subTransportType,
          actualTransportType: newQuote.submittedTransportType,
          quotingIntegrationName: integrationNameMap[Quoting.KencoFlash],
          quoteId: quote.id,
          isMultiStop,
          stops: convertFormStopsToResponseStops(formValues.stops),
          kencoMetadata: quote.metadata,
        });
        break;
      }
      case Quoting.Sonar: {
        newQuoteCards.push({
          type: SelectedQuoteType.SONAR,
          title: 'SONAR',
          icon: <SONARLogo />,
          cost: quote.rates.target,
          costPerMile: quote.rates.targetPerMile,
          confidence: quote.metadata?.confidenceScore || null,
          priceRange: {
            lowEstimate: quote.rates.low,
            highEstimate: quote.rates.high,
          } as PriceRangeType,
          priceRangePerMile: {
            lowEstimate: quote.rates.lowPerMile,
            highEstimate: quote.rates.highPerMile,
          } as PriceRangeType,
          inputtedTransportType: formValues.transportType,
          subTransportType,
          actualTransportType: newQuote.submittedTransportType,
          quotingIntegrationName: integrationNameMap[Quoting.Sonar],
          quoteId: quote.id,
          isMultiStop,
          stops: convertFormStopsToResponseStops(formValues.stops),
          sonarMetadata: quote.metadata,
        });
        break;
      }
      case Quoting.TruPay: {
        const card = parseTruPayQuoteCardFromResponse(
          quote,
          formValues,
          newQuote.submittedTransportType
        );
        if (card) {
          newQuoteCards.push(card);
        }
        break;
      }
      case Quoting.Transfix: {
        if (quote.type !== QuoteTypeInSource.TransfixSpot) {
          break;
        }

        newQuoteCards.push({
          type: SelectedQuoteType.TRANSFIX,
          title: integrationNameMap[Quoting.Transfix],
          icon: <TransfixLogo height={16} />,
          cost: quote.rates.target,
          costPerMile: quote.rates.targetPerMile,
          confidence: null,
          priceRange: {
            lowEstimate: quote.rates.low,
            highEstimate: quote.rates.high,
          } as PriceRangeType,
          priceRangePerMile: {
            lowEstimate: quote.rates.lowPerMile,
            highEstimate: quote.rates.highPerMile,
          } as PriceRangeType,
          inputtedTransportType: formValues.transportType,
          subTransportType,
          actualTransportType: newQuote.submittedTransportType,
          quotingIntegrationName: integrationNameMap[Quoting.Transfix],
          quoteId: quote.id,
          isMultiStop,
          stops: convertFormStopsToResponseStops(formValues.stops),
        });
        break;
      }
      case Quoting.SuperDispatch: {
        const metadata = quote.metadata?.superDispatch;
        // Poll only when the backend reports pending. Older hosts omit lane
        // data entirely and don't have a route to poll.
        const internalLaneContextStatus =
          metadata?.internalLaneContextStatus ??
          SuperDispatchInternalLaneContextStatus.Ready;
        const isLaneContextReady =
          internalLaneContextStatus ===
          SuperDispatchInternalLaneContextStatus.Ready;
        const rawConfidence =
          metadata?.confidence ?? quote.metadata?.confidenceLevel ?? null;
        // Super Dispatch uses 0 when no confidence score is available.
        const confidence =
          rawConfidence != null && rawConfidence > 0 ? rawConfidence : null;

        newQuoteCards.push({
          type: SelectedQuoteType.SUPER_DISPATCH,
          title: 'Pricing Insights',
          icon: null,
          cost: quote.rates.target,
          costPerMile: quote.rates.targetPerMile,
          confidence,
          priceRange:
            quote.rates.low > 0 && quote.rates.high > 0
              ? {
                  lowEstimate: quote.rates.low,
                  highEstimate: quote.rates.high,
                }
              : null,
          priceRangePerMile:
            quote.rates.lowPerMile > 0 && quote.rates.highPerMile > 0
              ? {
                  lowEstimate: quote.rates.lowPerMile,
                  highEstimate: quote.rates.highPerMile,
                }
              : null,
          inputtedTransportType: formValues.transportType,
          actualTransportType: newQuote.submittedTransportType,
          quotingIntegrationName: integrationNameMap[Quoting.SuperDispatch],
          quoteId: quote.id,
          stops: convertFormStopsToResponseStops(formValues.stops),
          superDispatchMetadata: {
            ...metadata,
            // The backend omits empty arrays. Once ready, a missing array means
            // none were found. Before then, leave it undefined so the card
            // shows loading or unavailable.
            recentPostings: isLaneContextReady
              ? (metadata?.recentPostings ?? [])
              : metadata?.recentPostings,
            priceTrend: isLaneContextReady
              ? (metadata?.priceTrend ?? [])
              : metadata?.priceTrend,
            confidence: confidence ?? undefined,
            internalLaneContextStatus,
          },
        });
        break;
      }
    }
  });
  return newQuoteCards;
}

function ratePerMilePriceRange(quote: CalculatedQuote): Maybe<PriceRangeType> {
  return quote.minRatePerMile && quote.maxRatePerMile
    ? ({
        lowEstimate: quote.minRatePerMile,
        highEstimate: quote.maxRatePerMile,
      } as PriceRangeType)
    : null;
}

// Catch-all selectable card for any TMS that lacks a dedicated SelectedQuoteType.*_LANE_HISTORY
function getGenericTmsLaneHistoryCard(
  history: SourceHistory,
  quote: CalculatedQuote
): Maybe<LaneHistoryCard> {
  if (!Object.values(TMS).includes(history.source as TMS)) {
    return null;
  }

  return {
    cardType: SelectedQuoteType.LANE_HISTORY,
    costPerMile: quote.avgRatePerMile,
    priceRangePerMile: ratePerMilePriceRange(quote),
  };
}

/** Pricing + card type for one TMS lane-history tier (chart toggle + quote card stay in sync). */
export function getLaneHistoryTierDisplay(history: SourceHistory) {
  const quote = history.calculatedQuote;
  if (!quote) return null;

  let cardType: SelectedQuoteType;
  let costPerMile: Undef<number>;
  let priceRangePerMile: Maybe<PriceRangeType>;

  switch (history.source) {
    case TMS.Aljex:
      cardType = SelectedQuoteType.ALJEX_LANE_HISTORY;
      costPerMile = quote.avgRatePerMile;
      priceRangePerMile = ratePerMilePriceRange(quote);
      break;
    case TMS.McleodEnterprise:
      cardType = SelectedQuoteType.MCLEOD_LANE_HISTORY;
      costPerMile = quote.avgRatePerMile;
      priceRangePerMile =
        quote.minCost && quote.maxCost
          ? ({
              lowEstimate: quote.minCost,
              highEstimate: quote.maxCost,
            } as PriceRangeType)
          : null;
      break;
    case TMS.Turvo:
      cardType = SelectedQuoteType.TURVO_LANE_HISTORY;
      costPerMile = quote.avgRatePerMile;
      priceRangePerMile = ratePerMilePriceRange(quote);
      break;
    case Quoting.GlobalTranz:
      cardType = SelectedQuoteType.GLOBALTRANZ_LANE_HISTORY;
      costPerMile = quote.avgRatePerMile;
      priceRangePerMile = ratePerMilePriceRange(quote);
      break;
    case TMS.GlobalTranzTMS:
      cardType = SelectedQuoteType.GLOBALTRANZ_TMS_LANE_HISTORY;
      costPerMile = quote.avgRatePerMile;
      priceRangePerMile = ratePerMilePriceRange(quote);
      break;
    default: {
      const genericCard = getGenericTmsLaneHistoryCard(history, quote);
      if (!genericCard) return null;
      cardType = genericCard.cardType;
      costPerMile = genericCard.costPerMile;
      priceRangePerMile = genericCard.priceRangePerMile;
      break;
    }
  }

  return {
    cardType,
    cost: quote.avgCost,
    costPerMile,
    priceRange:
      quote.minCost && quote.maxCost
        ? ({
            lowEstimate: quote.minCost,
            highEstimate: quote.maxCost,
          } as PriceRangeType)
        : null,
    priceRangePerMile,
    inputtedTransportType: history.inputtedTransportType,
    actualTransportType: history.proxiedTransportType,
    timeframe: history.timeframe + ' average',
    laneTier: history.laneTier!,
  };
}

function hasLaneHistoryPricing(history: SourceHistory): boolean {
  return getLaneHistoryTierDisplay(history) !== null;
}

// findLaneHistoryIndexWithPricing returns selected tier's index, if pricing is available
function findLaneHistoryIndexWithPricing(
  histories: SourceHistory[],
  tier: LaneTier
): number {
  const tierIndex = histories.findIndex((h) => h.laneTier === tier);
  if (tierIndex === -1 || !hasLaneHistoryPricing(histories[tierIndex])) {
    return -1;
  }
  return tierIndex;
}

// getDefaultLaneHistoryTierIndex returns the default tier's index;
// prefers Market-to-Market -> 3-Digit Zip -> first tier with pricing
export function getDefaultLaneHistoryTierIndex(
  histories: SourceHistory[]
): number {
  if (!histories.length) {
    return 0;
  }

  const marketIndex = findLaneHistoryIndexWithPricing(
    histories,
    LaneTier.MarketLaneTier
  );
  if (marketIndex !== -1) {
    return marketIndex;
  }

  const zip3Index = findLaneHistoryIndexWithPricing(
    histories,
    LaneTier._3DigitZipLaneTier
  );
  if (zip3Index !== -1) {
    return zip3Index;
  }

  const firstWithPricing = histories.findIndex(hasLaneHistoryPricing);
  if (firstWithPricing !== -1) {
    return firstWithPricing;
  }

  return 0;
}

export type TierFallbackBadge = { label: string; tooltip: string };

export function getFallbackTierBadge(
  histories: SourceHistory[],
  selectedLaneTier: LaneTier
): Undef<TierFallbackBadge> {
  if (selectedLaneTier !== LaneTier.StateLaneTier || !histories.length) {
    return undefined;
  }

  const defaultIndex = getDefaultLaneHistoryTierIndex(histories);
  const defaultTier = histories[defaultIndex]?.laneTier;

  if (defaultTier !== LaneTier.StateLaneTier) {
    return undefined;
  }

  const hasFinerTierPricing = [
    LaneTier.MarketLaneTier,
    LaneTier._3DigitZipLaneTier,
  ].some((tier) => findLaneHistoryIndexWithPricing(histories, tier) !== -1);

  if (hasFinerTierPricing) {
    return undefined;
  }

  return {
    label: 'Broad match',
    tooltip:
      'This lane rate is based on state-to-state history. It may be less specific than a market or zipcode-level match.',
  };
}

/**
 * Handles quote lane history from TMS sources
 */
export function handleQuoteLaneHistory(
  laneHistoryResponse: LaneHistoryResponse,
  originalStops: QuoteFormStop[]
): QuoteCardType[] {
  if (!laneHistoryResponse) return [];

  const laneHistoryRespQuotes: QuoteCardType[] = [];

  Object.values(laneHistoryResponse.resultsBySource).forEach((source) => {
    if (!source.length) return;

    const history = source[getDefaultLaneHistoryTierIndex(source)];
    const tier = getLaneHistoryTierDisplay(history);
    if (!tier) return;

    const quoteSourceIcon = (() => {
      switch (history.source) {
        case TMS.Aljex:
          return <AljexLogo height='auto' width={60} />;
        case TMS.McleodEnterprise:
          return <McleodLogo width={45} className='inline-block' />;
        case TMS.Turvo:
          return <TurvoLogo width={45} className='inline-block' />;
        case Quoting.GlobalTranz:
          return <GlobalTranzLogo height={12} width={75} />;
        case TMS.GlobalTranzTMS:
          return <TrifectaLogo height={16} width={86} />;
        case TMS.PLS:
          return <PLSLogo height={16} width={50} className='inline-block' />;
        case TMS.Revenova:
          return (
            <RevenovaLogo height={18} width={73} className='inline-block' />
          );
        case TMS.MercurygateBoomi:
          return (
            <MercuryGateLogo height={18} width={18} className='inline-block' />
          );
        default:
          return null;
      }
    })();

    laneHistoryRespQuotes.push({
      type: tier.cardType,
      // Existing dedicated TMS cards keep "Lane History" beside their logo.
      // SelectedQuoteType.LANE_HISTORY is the catch-all TMS card (logo optional).
      title:
        tier.cardType === SelectedQuoteType.LANE_HISTORY
          ? 'TMS Lane History'
          : 'Lane History',
      icon: quoteSourceIcon,
      cost: tier.cost,
      costPerMile: tier.costPerMile,
      confidence: null,
      priceRange: tier.priceRange,
      priceRangePerMile: tier.priceRangePerMile,
      tooltipContent: {
        isMultiStop: originalStops.length > 2,
        stops: originalStops,
        typeInSource: QuoteTypeInSource.TMSLaneHistory,
        timeframe: tier.timeframe,
        originName: 'Origin',
        originType: tier.laneTier,
        destinationName: 'Destination',
        destinationType: tier.laneTier,
      },
      tooltipConstructor: LaneHistoryTooltipConstructor,
      stops: convertFormStopsToResponseStops(originalStops),
      inputtedTransportType: tier.inputtedTransportType,
      actualTransportType: tier.actualTransportType,
      isMultiStop: false,
    });
  });

  return laneHistoryRespQuotes;
}
